/** @jsxImportSource @emotion/react */
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimateSharedLayout, motion } from "framer-motion";
import { atom, useAtom } from "jotai";
import localforage from "localforage";
import { nanoid } from "nanoid";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { isMobileSafari, isSafari } from "react-device-detect";
import {
  BsLayoutTextSidebar,
  BsLayoutTextSidebarReverse,
} from "react-icons/bs";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useSetRecoilState } from "recoil";
import { FormApi } from "../api/Form";
import { TagApi } from "../api/Tags";
import { TagsAtom } from "../atoms/TagsAtom";
import FormBuilderCheckbox from "../components/FormBuilder/FormBuilderCheckbox";
import FormBuilderEditor from "../components/FormBuilder/FormBuilderEditor";
import FormBuilderFooter from "../components/FormBuilder/FormBuilderFooter";
import FormBuilderHTML from "../components/FormBuilder/FormBuilderHTML";
import FormBuilderImage from "../components/FormBuilder/FormBuilderImage";
import FormBuilderInputs from "../components/FormBuilder/FormBuilderInputs";
import FormBuilderItem from "../components/FormBuilder/FormBuilderItem";
import FormBuilderList from "../components/FormBuilder/FormBuilderList";
import { ListOption } from "../components/FormBuilder/FormBuilderListSideBar";
import FormBuilderMain from "../components/FormBuilder/FormBuilderMain";
import FormBuilderSidebar from "../components/FormBuilder/FormBuilderSidebar";
import FormBuilderSideBarCard from "../components/FormBuilder/FormBuilderSideBarCard";
import FormBuilderTopBar from "../components/FormBuilder/FormBuilderTopBar";
import SeoHead from "../components/SeoHead";
import { TooltipText } from "../components/SideBar";
import useMediaQuery from "../hook/useMediaQuery";
import { customTooltip } from "../styles/general";
import { IForm } from "../types/FormTypes";
import { INTERNAL_UIDS_TO_SEE_CRM } from "../utils/constants";
import { splitAtomWithFallback } from "../utils/jotai/splitAtomWithFallback";
import { localStorageGetItem } from "../utils/LocalStorage";

const Container = styled.div<{ isPreview: boolean }>`
  display: grid;
  max-width: 100vw;
  width: 100%;
  grid-template-columns: 300px auto 300px;
  height: 100vh;
  min-height: 100%;
  overflow: hidden;
  padding-top: 81px;

  /* ! Gabiru */
  *::-webkit-scrollbar {
    z-index: 99;

    width: 6px !important;
    height: 6px !important;
  }

  *::-webkit-scrollbar-thumb {
    background-color: rgba(168, 168, 168, 0.64) !important;
    border-radius: 5px !important;
  }

  @media (max-width: 1680px) {
    display: flex;
  }

  ${(props) =>
    props.isPreview &&
    css`
      display: block;
      position: fixed;
      grid-template-columns: 1px auto 1px;
      padding-top: 0px;
    `}
`;

const Sidebar = styled(motion.div)<{ right?: boolean }>`
  width: 100%;
  max-width: 300px;
  height: calc(100vh - 81px);
  background-color: #fff;
  z-index: 2;

  @media (max-width: 1680px) {
    position: absolute;
    bottom: 0;

    ${(props) =>
      props.right &&
      css`
        right: 0;
      `}
  }
`;

const SideBarCards = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 115px);
  grid-gap: 20px;
  justify-content: center;
`;

export const SideBarText = styled.div`
  font-size: 14px;
  letter-spacing: 0.35px;
  color: #979797;
  font-weight: 600;
  margin: 25px 25px 18px;
`;

const SideBarButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  position: absolute;
  width: 30px;
  height: 30px;
  margin: 10px;
  z-index: 1;

  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 3px;

  cursor: pointer;

  transition: border-color 0.3s;

  svg {
    color: rgba(0, 0, 0, 0.16);

    transition: color 0.3s;
  }

  :hover {
    border-color: #fb972e;

    svg {
      color: #fb972e;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
`;

const Empty = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  img {
    width: 100px;
    height: 139px;
  }
`;

const EmptyText = styled.div`
  text-align: center;
  letter-spacing: 0px;
  color: #222222;
  font-size: 24px;
  font-weight: 600;
  margin-top: 20px;
`;

const EmptyDescription = styled.div`
  text-align: center;
  letter-spacing: 0px;
  color: #707070;
  font-size: 18px;
  margin-top: 7px;
`;

export type Types =
  | "TEXT"
  | "IMAGE"
  | "FORM"
  | "MAIN"
  | "CHECKBOX"
  | "FOOTER"
  | "HTML"
  | "LIST";

const TYPES: { label: string; type: Types; icon: string }[] = [
  {
    label: "Text",
    type: "TEXT",
    icon: "../icons/formBuilder/text.svg",
  },
  {
    label: "Image",
    type: "IMAGE",
    icon: "../icons/formBuilder/image.svg",
  },
  {
    label: "Checkbox",
    type: "CHECKBOX",
    icon: "../icons/formBuilder/checkbox.svg",
  },
  {
    label: "Form",
    type: "FORM",
    icon: "../icons/formBuilder/form.svg",
  },
  {
    label: "Footer",
    type: "FOOTER",
    icon: "../icons/formBuilder/footer.svg",
  },
  {
    label: "List",
    type: "LIST",
    icon: "../icons/formBuilder/list.svg",
  },
];

const THANK_YOU_TYPES = ["TEXT", "IMAGE", "FOOTER"];
export interface ItemType {
  id: string;
  type: Types;
  content?: typeof FormBuilderInputs | typeof FormBuilderEditor;
  style?: {
    fontFamily?: string;
    color?: string;
    padding?: string;
    margin?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: string;
    borderStyle?: string;
    checkbox?: boolean;
    fontSize?: string;
    width?: string;
    opacity?: string;
    borderRadius?: string;
    lineHeight?: string;
    paddingLeft?: string;
    paddingRight?: string;
    paddingTop?: string;
    paddingBottom?: string;
    marginLeft?: string;
    marginRight?: string;
    marginTop?: string;
    marginBottom?: string;
    linkColor?: string;
    visitedLinkColor?: string;
    checkboxColor?: string;
  };
  state?: any;
  deleted?: boolean;
}

export const indexesAtom = atom<number[]>([]);
export const itemsAtom = atom<ItemType[]>([]);

export const itemListAtom = splitAtomWithFallback(itemsAtom);
export const formAtom = atom<IForm | null>(null);
export const selectedItemAtom = atom<number | null>(0);
export const formBuilderCheckboxAtom = atom<boolean | null>(null);
export const formListOptionsAtom = atom<ListOption[]>([]);
export const isThankYouAtom = atom<boolean>(false);

function cleanUpStorage() {
  localforage.clear();
}

const MAIN_ITEM: ItemType = {
  id: "main",
  type: "MAIN",
  style: {},
};

const variantsRight = {
  open: {
    // opacity: 1,
    display: "block",
  },
  closed: {
    // opacity: 0,
    x: 300,
    transitionEnd: {
      display: "none",
    },
  },
};

const variantsLeft = {
  open: {
    // opacity: 1,
    display: "block",
  },
  closed: {
    // opacity: 0,
    x: -300,
    transitionEnd: {
      display: "none",
    },
  },
};

export default function FormBuilderScreen() {
  const router = useRouter();
  const { formId, preview, form: formFromQueryString, thankyou } = router.query;
  const [itemList, setItemsList] = useAtom(itemListAtom);
  const [indexes, setIndexes] = useAtom(indexesAtom);
  const [items, setItems] = useAtom(itemsAtom);
  const [selectedItem, setSelectedItem] = useAtom(selectedItemAtom);
  const [, setCheckbox] = useAtom(formBuilderCheckboxAtom);
  const [form, setForm] = useAtom(formAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreview, setIsPreview] = useState(false);
  const [overPosition, setOverPosition] = useState<{
    id: string;
    over: "TOP" | "BOTTOM";
  } | null>(null);
  const isLargerThan1680 = useMediaQuery("(min-width: 1680px)");
  const [showLeftSideBar, setShowLeftSideBar] = useState(false);
  const [showRightSideBar, setShowRightSideBar] = useState(false);
  const [isThankYou, setIsThankYou] = useAtom(isThankYouAtom);
  const [isChanging, setIsChanging] = useState(false);
  const alreadyLoadedItems = useRef(false);
  const alreadyLoadedIndexes = useRef(false);
  const alreadyLoaded = useRef(false);
  const setTagsPool = useSetRecoilState(TagsAtom);

  const [isToShowList, setIsToShowList] = useState(false);

  useEffect(() => {
    let internalUid = localStorageGetItem("INTERNAL_USER_UID") as string;

    if (INTERNAL_UIDS_TO_SEE_CRM.includes(internalUid)) setIsToShowList(true);
  }, []);

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  useEffect(() => {
    if (thankyou !== "") {
      setIsThankYou(false);
      return;
    }

    setIsThankYou(true);
  }, [thankyou]);

  // useEffect(() => {
  //   (async () => {
  //     let value = (await localforage.getItem("items")) as string;

  //     setItems(JSON.parse(value));
  //     setTimeout(() => (alreadyLoadedItems.current = true), 100);
  //   })();

  //   (async () => {
  //     let value = (await localforage.getItem("indexes")) as string;

  //     setIndexes(JSON.parse(value));
  //     setTimeout(() => (alreadyLoadedIndexes.current = true), 100);
  //   })();
  // }, []);

  useEffect(() => {
    getTags();

    return () => {
      if (!preview) {
        cleanUpStorage();
        setItems([]);
        setIndexes([]);
        setIsThankYou(false);
        //@ts-ignore
        setForm(null);
      }
    };
  }, []);

  useEffect(() => {
    if (!alreadyLoaded.current) return;

    (async () => {
      setIsChanging(true);

      let loadedItems = JSON.parse(
        (await localforage.getItem(
          isThankYou ? "thank-you-items" : "items"
        )) as string
      );

      let loadedIndexes = JSON.parse(
        (await localforage.getItem(
          isThankYou ? "thank-you-indexes" : "indexes"
        )) as string
      );

      if (!loadedItems) {
        loadedItems = isThankYou
          ? form.thankYouBuilder?.items
          : form.builder?.items;

        loadedIndexes = isThankYou
          ? form.thankYouBuilder?.indexes
          : form.builder?.indexes;
      }

      if (loadedItems && loadedIndexes) {
        const newItems = loadedItems.map((parsedItem) => {
          const { content } = checkType(parsedItem.type);

          return {
            ...parsedItem,
            content,
          };
        });

        setItems(newItems);
        // @ts-ignore
        setCheckbox(
          newItems.findIndex((i) => i.type === "CHECKBOX" && !i.deleted) === -1
        );
        setIndexes(loadedIndexes);
      } else {
        const initialArray = [];
        initialArray[0] = MAIN_ITEM;

        setItems(initialArray);
        setIndexes([0]);
        cleanUpStorage();
      }

      setIsChanging(false);
    })();
  }, [isThankYou]);

  useEffect(() => {
    // if (!alreadyLoaded.current) return;

    if (!alreadyLoadedItems.current) {
      alreadyLoadedItems.current = true;
      return;
    }

    localforage.setItem(
      isThankYou ? "thank-you-items" : "items",
      JSON.stringify(items)
    );
  }, [items]);

  useEffect(() => {
    // if (!alreadyLoaded.current) return;

    if (!alreadyLoadedIndexes.current) {
      alreadyLoadedIndexes.current = true;
      return;
    }

    localforage.setItem(
      isThankYou ? "thank-you-indexes" : "indexes",
      JSON.stringify(indexes)
    );
  }, [indexes]);

  useEffect(() => {
    (async () => {
      try {
        if (formId) {
          const formResponse = await FormApi.readOne(formId as string);
          // @ts-ignore
          setForm(formResponse);
        } else if (formFromQueryString) {
          const formResponse = await FormApi.readOneByName(
            (formFromQueryString as string).split("/")[0],
            (formFromQueryString as string).split("/")[1]
          );
          // @ts-ignore
          setForm(formResponse);
        } else {
          let href = window.location.href;
          if (
            !href.includes("localhost:3000") &&
            href.split("//")[1].split(".")[0] !== "app" &&
            href.split("//")[1].split(".")[0] !== "staging" &&
            href.split("//")[1].split(".")[0] !== "demo"
          ) {
            // Then I'm accessing through {APP_NAME}.berserkermail.com/{FORM_NAME}
            let userAppName = href.split("//")[1].split(".")[0];
            let formName = href.split("//")[1].split("/")[1];

            const formResponse = await FormApi.readOneByName(
              userAppName,
              formName
            );
            // @ts-ignore
            setForm(formResponse);
          }
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [formId, formFromQueryString]);

  useEffect(() => {
    if (!form) return;
    if (preview || formFromQueryString) setIsPreview(true);

    if (!formFromQueryString) {
      let href = window.location.href;
      if (
        !href.includes("localhost:3000") &&
        href.split("//")[1].split(".")[0] !== "app" &&
        href.split("//")[1].split(".")[0] !== "staging" &&
        href.split("//")[1].split(".")[0] !== "demo"
      )
        setIsPreview(true);
    }

    if (preview) {
      (async () => {
        const localItems = (await localforage.getItem(
          thankyou === "" ? "thank-you-items" : "items"
        )) as string;
        const localIndexes = (await localforage.getItem(
          thankyou === "" ? "thank-you-indexes" : "indexes"
        )) as string;

        if (localItems && localIndexes) {
          let parsedLocalItems: ItemType[] = JSON.parse(localItems);
          let parsedLocalIndexes: number[] = JSON.parse(localIndexes);

          const newItems = parsedLocalItems.map((parsedItem) => {
            const { content } = checkType(parsedItem.type);

            return {
              ...parsedItem,
              content,
            };
          });

          setItems(newItems);
          // @ts-ignore
          setCheckbox(
            newItems.findIndex((i) => i.type === "CHECKBOX" && !i.deleted) ===
              -1
          );
          setIndexes(parsedLocalIndexes);
        } else {
          cleanUpStorage();
        }

        setIsLoading(false);
      })();
    } else {
      const loadedItems =
        thankyou === "" ? form.thankYouBuilder?.items : form.builder?.items;

      const loadedIndexes =
        thankyou === "" ? form.thankYouBuilder?.indexes : form.builder?.indexes;

      if (loadedItems && loadedIndexes) {
        const newItems = loadedItems.map((parsedItem) => {
          const { content } = checkType(parsedItem.type);

          return {
            ...parsedItem,
            content,
          };
        });

        setItems(newItems);
        // @ts-ignore
        setCheckbox(
          newItems.findIndex((i) => i.type === "CHECKBOX" && !i.deleted) === -1
        );
        setIndexes(loadedIndexes);
      } else {
        const initialArray = [];
        initialArray[0] = MAIN_ITEM;

        setItems(initialArray);
        setIndexes([0]);
        cleanUpStorage();
      }

      setTimeout(() => (alreadyLoaded.current = true), 200);

      setIsLoading(false);
    }
  }, [form]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function hideSideBars() {
    setShowLeftSideBar(false);
    setShowRightSideBar(false);
  }

  function checkType(type: string): {
    type: Types;
    content: ItemType["content"];
  } {
    switch (type) {
      case "FORM":
        return { type: "FORM", content: FormBuilderInputs };
      case "IMAGE":
        return { type: "IMAGE", content: FormBuilderImage };
      case "CHECKBOX":
        return { type: "CHECKBOX", content: FormBuilderCheckbox };
      case "HTML":
        return { type: "HTML", content: FormBuilderHTML };
      case "LIST":
        return { type: "LIST", content: FormBuilderList };
      default:
        return { type: "TEXT", content: FormBuilderEditor };
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    // Remove the over indication
    setOverPosition(null);

    const id = nanoid();

    if (active.id === "FOOTER") {
      setItems([
        ...items,
        {
          id,
          type: "FOOTER",
        },
      ]);
      return;
    }

    if (indexes.length === 1) {
      const { content, type } = checkType(active.id);

      setItems([
        ...items,
        {
          id,
          type,
          content,
        },
      ]);

      setIndexes([...indexes, items.length]);
      //@ts-ignore
      setSelectedItem(1);

      return;
    }

    function getNewItem() {
      const oldIndex = indexes.findIndex((i) => i === parseInt(active.id));
      const newIndex = indexes.findIndex((i) => i === parseInt(over!.id));

      // I'm dropping something that's not yet on the array. I'm ADDING it
      if (oldIndex === -1) {
        let indexToBeInserted = newIndex;
        if (overPosition?.over === "BOTTOM") {
          indexToBeInserted += 1;
        }

        const id = nanoid();

        const { content, type } = checkType(active.id);

        // At the top of the list
        if (indexToBeInserted === 0) {
          return {
            newIndexes: [items.length, ...indexes],
            newItems: [
              ...items,
              {
                id,
                content,
                type,
              },
            ],
          };
        }

        // At the bottom of the list
        if (indexToBeInserted === items.length) {
          return {
            newIndexes: [...indexes, items.length],
            newItems: [
              ...items,
              {
                id,
                content,
                type,
              },
            ],
          };
        }

        const indexesCopy = [...indexes];

        indexesCopy.splice(indexToBeInserted, 0, items.length);
        return {
          newIndexes: indexesCopy,
          newItems: [
            ...items,
            {
              id,
              content,
              type,
            },
          ],
        };
      }

      return {
        newIndexes: arrayMove(indexes, oldIndex, newIndex),
        newItems: items,
      };
    }

    if (!over) return;

    // Find where to insert it
    if (active.id !== over.id) {
      const { newIndexes, newItems } = getNewItem();

      setItems(newItems);
      setIndexes(newIndexes);
    }
  }

  // Function to detect the item that I'm moving is relative to what
  // item already in the list, and if it should be added
  function itemMoveRelativePosition(event: DragMoveEvent) {
    const { active, over } = event;

    // If I'm moving an item on the list, ignore
    if (!TYPES.find((t) => t.type === active.id)) return;

    if (!over) return;

    let activeY = active.rect.current.translated?.offsetTop;
    let middleOfOver = over.rect.offsetTop + over.rect.height / 2;

    if (!activeY) return;
    if (activeY > middleOfOver) {
      setOverPosition({
        id: over.id,
        over: "BOTTOM",
      });
    } else {
      setOverPosition({
        id: over.id,
        over: "TOP",
      });
    }
  }

  useEffect(() => {
    if (isLoading || !form || !isPreview || (!isSafari && !isMobileSafari))
      return;

    let node = document.querySelector("#__next");
    document.body.removeChild(node);
    document.body.parentElement.prepend(node);
  }, [isLoading, form]);

  if (isLoading || !form) return <></>;
  return (
    <>
      <SeoHead pageName={isPreview ? form.name : "Form Builder"} />
      {!isPreview && <FormBuilderTopBar />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragMove={itemMoveRelativePosition}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setOverPosition(null)}
        onDragStart={() => {
          setSelectedItem(null);
        }}
      >
        <Container isPreview={isPreview}>
          {!isPreview && (
            <SideBarButton
              css={css``}
              onClick={(e) => {
                setShowLeftSideBar(!showLeftSideBar);
                e.stopPropagation();
              }}
              data-tip
              data-for="left-side-bar-element"
            >
              <BsLayoutTextSidebarReverse />

              <ReactTooltip
                id="left-side-bar-element"
                place="right"
                effect="solid"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>Element sidebar</TooltipText>
              </ReactTooltip>
            </SideBarButton>
          )}
          {!isPreview && (
            <Sidebar
              css={css`
                border-right: 1px solid rgba(0, 0, 0, 0.16);
                position: relative;
              `}
              animate={isLargerThan1680 || showLeftSideBar ? "open" : "closed"}
              variants={variantsLeft}
              transition={{
                bounce: false,
              }}
            >
              <SideBarText>FORM ELEMENTS</SideBarText>
              <SideBarCards>
                {TYPES.map((type) => {
                  if (isThankYou && !THANK_YOU_TYPES.includes(type.type))
                    return;

                  // if (type.type === "LIST" && !isToShowList) return;

                  return (
                    <FormBuilderSideBarCard
                      key={type.type}
                      {...type}
                      alreadyExists={
                        items.findIndex(
                          (i) => i.type === type.type && !i.deleted
                        ) !== -1
                      }
                    />
                  );
                })}
              </SideBarCards>
            </Sidebar>
          )}

          {itemList[0] && (
            <FormBuilderMain
              onClick={(e) => {
                hideSideBars();
                e.stopPropagation();
                //@ts-ignore
                setSelectedItem(0);
              }}
              key={0}
              index={0}
              itemAtom={itemList[0]}
              isPreview={!!isPreview}
              isSelected={selectedItem === 0}
            >
              {!items.find((i) => i.type === "FOOTER" && !i.deleted) &&
              indexes.length === 1 ? (
                <Empty>
                  <img src="./images/empty-builder.svg" />
                  <EmptyText>Form Builder</EmptyText>
                  <EmptyDescription>
                    Select or drag and drop a form element here to get started.
                  </EmptyDescription>
                </Empty>
              ) : (
                <Wrapper>
                  <Content>
                    <SortableContext
                      items={indexes
                        .filter((index) => index > 0)
                        .map((item) => item + "")}
                      strategy={verticalListSortingStrategy}
                    >
                      <AnimateSharedLayout>
                        {!isChanging &&
                          indexes.map((index) =>
                            index > 0 && items[index].type !== "FOOTER" ? (
                              <FormBuilderItem
                                over={
                                  overPosition?.id === index.toString()
                                    ? overPosition.over
                                    : null
                                }
                                key={index}
                                index={index}
                                itemAtom={itemList[index]}
                                isPreview={!!isPreview}
                                onClick={(e) => {
                                  hideSideBars();
                                  e.stopPropagation();
                                  !isPreview && setSelectedItem(index);
                                }}
                                isSelected={selectedItem === index}
                              />
                            ) : null
                          )}
                      </AnimateSharedLayout>
                    </SortableContext>
                  </Content>
                  {!isChanging &&
                    items.find((i) => i.type === "FOOTER" && !i.deleted) && (
                      <FormBuilderFooter
                        key="footer"
                        index={items.findIndex(
                          (i) => i.type === "FOOTER" && !i.deleted
                        )}
                        isSelected={
                          selectedItem ===
                          items.findIndex(
                            (i) => i.type === "FOOTER" && !i.deleted
                          )
                        }
                        isPreview={!!isPreview}
                        onClick={(e) => {
                          hideSideBars();
                          e.stopPropagation();
                          //@ts-ignore
                          setSelectedItem(
                            items.findIndex(
                              (i) => i.type === "FOOTER" && !i.deleted
                            )
                          );
                        }}
                      />
                    )}
                </Wrapper>
              )}
            </FormBuilderMain>
          )}

          {!isPreview && (
            <SideBarButton
              css={css`
                right: 0;
              `}
              onClick={(e) => {
                setShowRightSideBar(!showRightSideBar);
                e.stopPropagation();
              }}
              data-tip
              data-for="right-side-bar-style"
            >
              <BsLayoutTextSidebar />

              <ReactTooltip
                id="right-side-bar-style"
                place="right"
                effect="solid"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>Style sidebar</TooltipText>
              </ReactTooltip>
            </SideBarButton>
          )}
          {!isPreview && (
            <Sidebar
              css={css`
                border-left: 1px solid rgba(0, 0, 0, 0.16);
                overflow-y: auto;
              `}
              right
              animate={isLargerThan1680 || showRightSideBar ? "open" : "closed"}
              variants={variantsRight}
              transition={{
                bounce: false,
              }}
            >
              {!isPreview && !!itemList[selectedItem] && (
                <FormBuilderSidebar itemAtom={itemList[selectedItem]} />
              )}
            </Sidebar>
          )}
        </Container>
      </DndContext>
    </>
  );
}
